/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */

import {
  CapNames,
  Director,
  Stage,
  DefaultCaps,
  DefaultNavigators,
  Materials,
  CameraDesc,
  CapDataDesc,
  Text2DFontLoader,
  Text2DFont,
  FloorKind,
  SceneCompass,
  FloorDesc,
} from '@sick-davinci/glow';
import {
  CrownViewCapData,
  CrownPointCloudCap,
  CrownTextCap,
  CrownShapeCap,
  CrownMeshCap,
  CrownImageCap,
  CrownScanCap,
  CrownHeightmapCap,
  CrownViewPresentUpdateDecorationDesc,
  CrownShapeDecorationDesc,
  CrownTransform,
} from '@sick-davinci/glow-crown';
import { CrownConnectionWebSocket, CrownProtocolMsgPack, LogLevel, CrownProtocolJSON } from '@sick-davinci/device-communication';
import { Mesh, BoxBufferGeometry, LineSegments, EdgesGeometry, LineBasicMaterial } from 'three';

import { Button } from '@sick-davinci/basic-elements';

export const dependencies = [Button];  // avoid wepack removal

/**
 * Demo:    CrownUpdateDecorations
 * Description:
 *   This demo application shows how decorations of existing objects can be updated via Crown.
 *   Updating only the decoration of an objects saves data and CPU for displaying the original object.
 *   Use the included app with an AppEngine to see how decorations are updated with the CrownView interface.
 *   Without the app the user use the UI button to switch between random decorations.
 *   If connected the app creates several iconics which change their decoration alternately.
 * Features:
 *  - Orbit navigation
 *  - Capability handling
 *  - Scene compass
 *  - ViewCrown / WebSocket connection
 *  - Native threejs code
 */
export const run = (async (viewParent: HTMLElement) => {

  // ** setup the stage *
  const camDesc: CameraDesc = {
    name: 'default',
    from: [0, 20, 100],
    to: [0, 0, 0],
    up: [0, 1, 0],
    fov: 50,
  };

  const stage = new Stage(viewParent, {
    cameras: [camDesc],
    scenes: [{ name: 'default' }],
    viewports: [{
      name: 'default',
      scene: 'default',
      camera: 'default',
      navigator: 'orbit',
      left: 0,
      top: 0,
      width: 1,
      height: 1,
      bgcolor: 0x393939,
    }],
  });

  // * Add the scene compass for better orientation
  SceneCompass.create(stage, 'default', 'default');

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit');


  // * Data handling using capabilities
  const director = new Director(stage);

  director.addCapability(new CrownShapeCap());
  director.addCapability(new CrownMeshCap());
  director.addCapability(new CrownImageCap());
  director.addCapability(new CrownScanCap());
  director.addCapability(new CrownHeightmapCap());
  director.addCapability(new CrownPointCloudCap());
  director.addCapability(await createTextCap());

  DefaultCaps.init(director);

  // * Add some light so there is smth to see
  director.handle(CapNames.LIGHT, { lightType: 'point', color: 0xfffff0, orientation: { position: [1500, 800, -1300] } });
  director.handle(CapNames.LIGHT, { lightType: 'ambient', color: 0x888888 });

  // * Add a floor for better orientation
  director.handle(CapNames.FLOOR, {
    payload: {
      floor: FloorKind.WIREFRAME,
      size: 256,
      segments: 128,
      color: 0x555555,
    },
    orientation: {
      position: [0, -.01, 0],
    },
  } as CapDataDesc<FloorDesc>);

  // * Add a box using native threejs code
  stage.applyThreeDirect((scene) => {
    const box = createBox(0xe0e0e0, [10, 10, 10], [-10, 5, 0]);
    box.name = 'shape';
    scene.add(box);
  });

  stage.start();  // start visualizing the stage

  // ** Connect to the device via the ViewCrown using the provided ip and view-id
  const cc = new CrownConnectionWebSocket('127.0.0.1', 'crownJSON', 3000);
  const cp = new CrownProtocolJSON();
  const view = new CrownViewCapData(cc, cp, '3D');

  // ** Set all necessary callback functions
  view.onPresentStart(() => stage.lockRenderer());
  view.onPresentEnd(() => stage.unlockRenderer());
  view.onPresentClear(() => stage.clearAllScenes());
  view.onPresentRemove((id) => stage.clearObject(stage.getObjectIdByName(id)));
  view.onPresentUpdateDecorationCapData((capData) => {
    director.handleCapData(capData);
  });
  view.onPresentAddCapData((capData) => {
    director.handleCapData(capData);
  });
  view.onConnectionChanged((connected) => {
    (document.getElementById('updatedeco') as Button).disabled = connected;
    view.logMessage(LogLevel.INFO, 'Viewer connected.');
  });
  view.onPresentUpdateTransform((id, transform) => {
    const obj = stage.getObjectByName(id);
    if (!!obj) {
      CrownTransform.copyMatrix(transform, obj);
    }
  });

  // ** setup user interaction */
  const update = document.getElementById('updatedeco') as Button;
  update.addEventListener('click', ((evt) => {
    const capData = getRandomShapeDecoration();
    director.handleCapData(capData);
  }), true);

  // * create shape decorations in a random way each time the user presses the button
  function getRandomShapeDecoration() {
    // ** update a shape decoration */
    const capData: CapDataDesc<CrownViewPresentUpdateDecorationDesc<CrownShapeDecorationDesc>> = {
      payload: {
        IconicId: 'shape',
        Type: 'View.ShapeDecoration',
        Decoration: {
          class: 'View.ShapeDecoration',
          data: {
            FillColor: { A: Math.random() * 255, R: Math.random() * 255, G: Math.random() * 255, B: 100 },
            LineColor: { A: 100, R: 0, G: 200, B: 200 },
            LineWidth: Math.random() * 5,
            PointSize: 1,
            PointType: 'DOT',
          },
        },
      },
      name: 'shape',
      funcName: 'update',
    };
    return capData;
  }
});

function createBox(color: number, size: [number, number, number], position: [number, number, number]) {
  const box =
    new Mesh(
      new BoxBufferGeometry(...size),
      Materials.create({ type: 'normal' }),
    );
  const line = new LineSegments(new EdgesGeometry(box.geometry), new LineBasicMaterial());
  box.add(line);
  box.position.set(...position);
  box.updateMatrix();
  return box;
}

// * create text cap with necessary font resources *
async function createTextCap() {
  const baseUrl = './resources/Text2D/';
  const fonts = {
    SANSSERIF: await Text2DFontLoader.getFont(baseUrl + 'OpenSans-Regular.json', baseUrl),
    SERIF: await Text2DFontLoader.getFont(baseUrl + 'NotoSerifJP-Regular.json', baseUrl),
    MONOSPACE: await Text2DFontLoader.getFont(baseUrl + 'CutiveMono-Regular.json', baseUrl),
  };
  return new CrownTextCap(fonts);
}
